import React, { useRef, useLayoutEffect } from 'react';

/**
 * Auto-growing textareas that resize to fit their content.
 *
 * WHY THIS EXISTS (the bug it prevents):
 * The naive way to auto-size a textarea is a ref callback that measures and
 * sets height, e.g. `ref={(el) => { el.style.height = '0px'; el.style.height =
 * el.scrollHeight + 'px'; }}`. That callback runs on EVERY React render, not
 * just on mount. So whenever anything causes the parent to re-render — most
 * commonly the clinician typing in a *different* field on the same page — every
 * such textarea momentarily collapses to 0px and then re-expands. The transient
 * height change shifts document layout and the browser yanks the scroll
 * position, so the user gets "jumped" up the page while typing.
 *
 * The robust pattern (identical to the shared `DictationTextArea`) is to
 * recalculate height inside a `useLayoutEffect` keyed on `value`: it runs before
 * paint (so the user never sees a stale height) and ONLY when the text actually
 * changes — never because a sibling re-rendered.
 *
 * Two variants are provided so callers do not each re-implement this:
 *   - `AutoResizeTextarea`      — editable; takes onChange/onBlur/etc.
 *   - `ReadOnlyAutoResizeTextarea` — read-only summary display.
 *
 * These are intentionally "bare" (no container chrome) and accept a full
 * `className`, so they slot into existing inline-styled layouts. Prefer
 * `DictationTextArea` for the standard bordered clinician-notes box; use these
 * where the surrounding markup already provides the styling.
 */

/** Shared height-to-content reflow, before paint, only when `value` changes. */
function useAutoHeight(ref, value) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Set to 0 first so scrollHeight reflects the content height and the field
    // can shrink as well as grow. A min-height CSS class (if any) still governs
    // the visible minimum.
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [ref, value]);
}

/**
 * Read-only auto-height textarea (e.g. summary panels showing saved notes).
 *
 * @param {Object} props
 * @param {string} props.value
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 * @param {Object} [props.style]
 */
export function ReadOnlyAutoResizeTextarea({ value, className = '', ariaLabel, style = {} }) {
  const ref = useRef(null);
  useAutoHeight(ref, value);

  return (
    <textarea
      readOnly
      ref={ref}
      value={value}
      className={className}
      style={{ overflow: 'hidden', ...style }}
      tabIndex={-1}
      aria-label={ariaLabel}
    />
  );
}

/**
 * Editable auto-height textarea.
 *
 * `onInput` also reflows synchronously on each keystroke so growth is instant
 * (before React commits the controlled value); the `useLayoutEffect` covers
 * programmatic value changes (initial load, external updates).
 *
 * @param {Object} props
 * @param {string} props.value
 * @param {(e: Event) => void} [props.onChange] - native change handler (receives the event)
 * @param {(e: Event) => void} [props.onBlur]
 * @param {(e: Event) => void} [props.onClick]
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 * @param {number} [props.rows]
 * @param {Object} [props.style]
 */
export function AutoResizeTextarea({
  value,
  onChange,
  onBlur,
  onClick,
  placeholder,
  disabled = false,
  className = '',
  ariaLabel,
  rows = 3,
  style = {},
}) {
  const ref = useRef(null);
  useAutoHeight(ref, value);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onClick={onClick}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      style={{ overflow: 'hidden', ...style }}
      rows={rows}
      aria-label={ariaLabel}
      onInput={(e) => {
        e.target.style.height = '0px';
        e.target.style.height = `${e.target.scrollHeight}px`;
      }}
    />
  );
}

export default AutoResizeTextarea;
